import { type Href } from 'expo-router';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import type { OnboardingStepsContextType } from '../types/onboardingStepsContextType';
import { hrefPathname } from '../utils/hrefPathname';
import { getStepContext } from '../utils/onboardingStepProgress';

import { RulesEngineContext } from './RulesEngineContext';

export const OnboardingStepsContext = createContext<OnboardingStepsContextType | null>(null);

/**
 * Owns step-navigation state (`stepHistory`, `goBack`) — everything that's a
 * pure function of `currentRoute`. Nests INSIDE RulesEngineProvider and reads
 * `currentRoute` / `setCurrentRoute` / `authStatus` from it: routing decisions
 * read domain state, so the router/history layer sits below the domain that
 * drives it. Forward navigation (`goToNextStep`) stays in the rules engine
 * (it needs domain state); `useOnboardingSteps` recombines both.
 */
export const OnboardingStepsProvider = ({ children }: { children: React.ReactNode }) => {
  const rulesEngine = useContext(RulesEngineContext);
  if (!rulesEngine) {
    throw new Error('OnboardingStepsProvider must be used within a RulesEngineProvider');
  }
  const { currentRoute, setCurrentRoute, authStatus, setSelectedOrg } = rulesEngine;

  const [stepHistory, setStepHistory] = useState<string[]>([]);

  // Back-navigation action. Targets the most recent in-group step from history;
  // if history has none (deep link / refresh lost it) it falls back to the
  // group's first step, so back still works from a directly-loaded branch.
  // No-op for forward-only groups or when already at the group entry. Mirrors
  // Sparta's PREVIOUS_STEP reducer. Reads `stepHistory` from deps (not via the
  // updater form) so the `setCurrentRoute` side-effect doesn't double-fire under
  // strict mode's double-invoked updaters.
  const goBack = useCallback(() => {
    if (stepHistory.length === 0) return;
    const currentPath = stepHistory[stepHistory.length - 1];
    const ctx = getStepContext(currentPath);
    if (!ctx || ctx.group.direction !== 'bi-directional') return;
    const groupPaths = new Set(ctx.group.steps.map((s) => s.path));

    let target: string | null = null;
    let prunedHistory = stepHistory;
    for (let i = stepHistory.length - 2; i >= 0; i -= 1) {
      if (groupPaths.has(stepHistory[i])) {
        target = stepHistory[i];
        prunedHistory = stepHistory.slice(0, i + 1);
        break;
      }
    }
    if (!target) {
      const firstStep = ctx.group.steps[0].path;
      if (currentPath === firstStep) return; // already at the group entry
      target = firstStep;
      prunedHistory = [firstStep];
    }

    // Going back to company-search means de-selecting the org: clear it so the
    // driver doesn't immediately re-derive forward to the org's entry screen
    // (e.g. /employer-or-health-plan-verification?org=vida). Combined with the
    // bare `/step/company-search` target, this also drops the `?org=` param.
    if (target === '/step/company-search') {
      setSelectedOrg(undefined);
    }
    setStepHistory(prunedHistory);
    setCurrentRoute(target as Href);
  }, [stepHistory, setCurrentRoute, setSelectedOrg]);

  // Single source of truth for stepHistory, keyed on currentRoute + authStatus:
  //   - On sign-out / 401 (authStatus 'unauthenticated') clear the stack. The
  //     reset can't live in the rules engine's resetUserData anymore (history
  //     is owned here), and folding it into this one effect avoids racing the
  //     push below when resetUserData also flips currentRoute.
  //   - Otherwise push the new path. Dedupe when it matches the top — happens
  //     after goBack updates both currentRoute and stepHistory in the same
  //     render; this effect would otherwise re-add what goBack just popped.
  useEffect(() => {
    if (authStatus === 'unauthenticated') {
      setStepHistory((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    const path = hrefPathname(currentRoute);
    if (!path) return;
    setStepHistory((prev) => {
      if (prev[prev.length - 1] === path) return prev;
      return [...prev, path];
    });
  }, [currentRoute, authStatus]);

  // Value is not wrapped in useMemo — matches RulesEngineProvider's rationale:
  // it would regenerate whenever stepHistory/goBack change anyway, and
  // consumers (useOnboardingSteps) destructure the fields rather than relying
  // on the object identity.
  return (
    <OnboardingStepsContext.Provider value={{ stepHistory, goBack }}>
      {children}
    </OnboardingStepsContext.Provider>
  );
};
